/**
 * SASADM-0159 — Import bundle (saas-auth SASATH-0351 §3.2/§3.3).
 *
 * Step 1: choose a `.zip`. Over 10 MB it is refused client-side (the server
 * may drop the connection instead of answering 413); otherwise an explicit
 * Check bundle sends the dry run. Step 2: the report — status, changes,
 * warnings, contrast (the Theme tab's row component) and per-file errors.
 * Apply is enabled only when `report.ok`, and re-sends the SAME file object.
 * An apply error folds back into the report (400 errors, 409 etag, 422
 * contrast). The client never reads the zip; the server is authoritative.
 * SASADM-0160: step 1 says to zip the files, not their folder, and a report
 * whose errors match a folder zip shows one message with the rows under Details.
 */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState, type ChangeEvent, type ReactNode } from 'react';
import { apiAdmin } from '@/api/admin';
import { BUNDLE_MAX_BYTES } from '@/api/admin/brandBundle';
import { DEFAULT_RETRY_AFTER_SECONDS } from '@/api/client';
import { ApiError } from '@/api/errors';
import type { BundleError, BundleReport, ContrastFailure } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';
import { ContrastFailureRow } from './ContrastFailureRow';
import { failureKey } from './contrastFailureKey';

const HIGHLIGHTED_WARNINGS = new Set(['BUNDLE_CHANGES_IDENTITY', 'BUNDLE_FROM_OTHER_BRAND']);
const ASSET_GROUPS = ['added', 'replaced', 'cleared', 'unchanged'] as const;
const FOLDER_ZIP_CODES = new Set(['ILLEGAL_PATH', 'UNKNOWN_ENTRY']);

/**
 * SASADM-0160 — a "compress folder" zip (0351 F4): every row is ILLEGAL_PATH /
 * UNKNOWN_ENTRY and either a file is macOS metadata (`__MACOSX/…`), or every
 * nested file sits under one top-level folder. `assets/` is the bundle's own
 * folder, so it never counts as the wrapping one.
 * SASADM-0161 — only real nesting counts: top-level files (a stray `.DS_Store`,
 * `notes.txt`) are ignored, so a flat zip's `.DS_Store` is just a normal row.
 */
function isFolderZip(errors: BundleError[]): boolean {
  if (errors.length === 0 || !errors.every((e) => FOLDER_ZIP_CODES.has(e.code))) return false;
  const files = errors.map((e) => e.file).filter((f): f is string => f !== null);
  if (files.some((f) => f.startsWith('__MACOSX/'))) return true;
  const folders = new Set(files.filter((f) => f.includes('/')).map((f) => f.split('/')[0]));
  const [folder] = folders;
  return folders.size === 1 && folder !== 'assets';
}

/**
 * SASADM-0162 — a flat zip rejected only for stray macOS `.DS_Store` files
 * (top level or under `assets/`, each an UNKNOWN_ENTRY). A folder zip wins.
 */
function isDsStoreOnly(errors: BundleError[]): boolean {
  if (errors.length === 0 || isFolderZip(errors)) return false;
  return errors.every(
    (e) => e.code === 'UNKNOWN_ENTRY' && e.file !== null && e.file.split('/').pop() === '.DS_Store',
  );
}

function rawError(err: ApiError): { errors?: unknown; failures?: unknown } | undefined {
  return (err.details.raw as { error?: { errors?: unknown; failures?: unknown } } | undefined)
    ?.error;
}

interface BrandBundleDialogProps {
  brandId: number;
  onClose: () => void;
  /** Called after a 200 apply; the page refreshes the theme, copy-locales and brand. */
  onApplied: () => void;
}

export function BrandBundleDialog({ brandId, onClose, onApplied }: BrandBundleDialogProps) {
  const { t } = useCopy();
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<BundleReport | null>(null);
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [highlighted, setHighlighted] = useState<string | null>(null);

  const tooLarge = file !== null && file.size > BUNDLE_MAX_BYTES;
  const busy = checking || applying;

  function reset() {
    setFile(null);
    setReport(null);
    setError(null);
    setHighlighted(null);
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!picked) return;
    setFile(picked);
    setError(null);
  }

  async function handleCheck() {
    if (!file || tooLarge) return;
    setChecking(true);
    setError(null);
    try {
      setReport(await apiAdmin.brandBundle.dryRun(brandId, file));
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError('UNKNOWN', 0));
    } finally {
      setChecking(false);
    }
  }

  async function handleApply() {
    if (!file || !report?.ok) return;
    setApplying(true);
    setError(null);
    try {
      await apiAdmin.brandBundle.apply(brandId, file);
      onApplied();
    } catch (err) {
      if (!(err instanceof ApiError)) {
        setError(new ApiError('UNKNOWN', 0));
        return;
      }
      // §3.2 precedence: 400 BUNDLE_INVALID → 409 THEME_CHANGED → 422 contrast.
      const raw = rawError(err);
      if (err.status === 400 && err.code === 'BUNDLE_INVALID') {
        const errors = Array.isArray(raw?.errors) ? (raw.errors as BundleError[]) : [];
        setReport((r) => (r ? { ...r, ok: false, errors } : r));
      } else if (err.status === 409 || err.code === 'THEME_CHANGED') {
        setReport((r) => (r ? { ...r, ok: false, etagMatches: false } : r));
        return;
      } else if (err.status === 422 || err.code === 'THEME_CONTRAST_FAILED') {
        const failures = Array.isArray(raw?.failures) ? (raw.failures as ContrastFailure[]) : [];
        setReport((r) => (r ? { ...r, ok: false, contrast: { ok: false, failures } } : r));
      }
      setError(err);
    } finally {
      setApplying(false);
    }
  }

  function errorMessage(err: ApiError): string {
    if (err.status === 413 || err.code === 'BUNDLE_TOO_LARGE') {
      return t('brands.bundle.tooLarge', 'This bundle is over 10 MB');
    }
    if (err.status === 429) {
      return t('brands.bundle.rateLimited', 'Too many bundle checks — try again in {{n}} s', {
        n: err.details.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS,
      });
    }
    if (err.code === 'BUNDLE_INVALID') {
      return t('brands.bundle.applyInvalid', 'The bundle has errors. Nothing was applied.');
    }
    if (err.status === 422 || err.code === 'THEME_CONTRAST_FAILED') {
      return t(
        'brands.bundle.applyContrast',
        'A colour in this bundle fails WCAG AA contrast. Nothing was applied.',
      );
    }
    return (
      err.details.message ??
      t(`errors.codes.${err.code}`, t('errors.generic', 'Something went wrong.'))
    );
  }

  function section(title: string, items: string[], id: string): ReactNode {
    return (
      <Box key={id} data-testid={`bundle-changes-${id}`}>
        <Typography variant="subtitle2">{title}</Typography>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('brands.bundle.noChanges', 'No changes')}
          </Typography>
        ) : (
          <Box component="ul" sx={{ my: 0, pl: 3 }}>
            {items.map((item) => (
              <Typography
                key={item}
                component="li"
                variant="body2"
                sx={{ fontFamily: 'monospace' }}
              >
                {item}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  function errorRows(errors: BundleError[]): ReactNode {
    return (
      <Box component="ul" sx={{ my: 0, pl: 3 }}>
        {errors.map((e, i) => (
          <Typography
            // Rows have no id; file/path/code can repeat across a report.
            key={`${e.file ?? ''}|${e.path ?? ''}|${e.code}|${i}`}
            component="li"
            variant="body2"
          >
            {[e.file ?? t('brands.bundle.wholeBundle', 'Whole bundle'), e.path, e.message]
              .filter((part): part is string => Boolean(part))
              .join(' · ')}
          </Typography>
        ))}
      </Box>
    );
  }

  function renderReport(r: BundleReport): ReactNode {
    const reasons: string[] = [];
    if (r.errors.length > 0) {
      reasons.push(t('brands.bundle.reasonErrors', 'The bundle has errors — see Errors below.'));
    }
    if (r.etagMatches === false) {
      reasons.push(
        t(
          'brands.bundle.etagChanged',
          'This brand changed since the bundle was exported — export again.',
        ),
      );
    }
    if (!r.contrast.ok) {
      reasons.push(
        t('brands.bundle.reasonContrast', 'A colour in this bundle fails WCAG AA contrast.'),
      );
    }
    // After a whole-zip or manifest error the server couldn't read
    // `manifest.brand`, so a null `etagMatches` means "not compared", not
    // "another brand" — the note is suppressed then.
    const manifestUnread = r.errors.some((e) => e.file === null || e.file === 'manifest.json');
    const { theme, copy, assets } = r.changes;
    const assetCount = ASSET_GROUPS.reduce((n, g) => n + assets[g].length, 0);
    // One plain alert for a re-zip problem; the folder alert takes precedence.
    const zipAlert = isFolderZip(r.errors)
      ? {
          testId: 'bundle-folder-zip',
          text: t(
            'brands.bundle.folderZip',
            'This zip contains a folder (or macOS extras) instead of the bundle files at the top level. Re-zip the files themselves and choose it again.',
          ),
        }
      : isDsStoreOnly(r.errors)
        ? {
            testId: 'bundle-ds-store',
            text: t(
              'brands.bundle.dsStore',
              "This zip includes macOS .DS_Store files. Remove them and re-zip — the bundle's README (Zip the files, not their folder) has the command.",
            ),
          }
        : null;
    return (
      <Stack spacing={2}>
        {r.ok ? (
          <Alert severity="success" data-testid="bundle-status">
            {t('brands.bundle.statusReady', 'Ready to apply')}
          </Alert>
        ) : (
          <Alert severity="error" data-testid="bundle-status">
            <Typography variant="subtitle2">
              {t('brands.bundle.statusBlocked', "Can't apply")}
            </Typography>
            {reasons.map((reason) => (
              <Typography key={reason} variant="body2">
                {reason}
              </Typography>
            ))}
          </Alert>
        )}
        {r.etagMatches === null && !manifestUnread ? (
          <Alert severity="info">
            {t('brands.bundle.otherBrand', 'This bundle was exported from another brand.')}
          </Alert>
        ) : null}

        <Box>
          <Typography variant="h3" gutterBottom>
            {t('brands.bundle.changes', 'Changes')}
          </Typography>
          <Stack spacing={1.5}>
            {section(t('brands.bundle.theme', 'Theme'), theme.changed, 'theme')}
            {section(t('brands.bundle.copyDefault', 'Default text'), copy.default, 'copy-default')}
            {Object.entries(copy.locales).map(([locale, keys]) =>
              section(
                t('brands.bundle.copyLocale', 'Translations ({{locale}})', { locale }),
                keys,
                `copy-${locale}`,
              ),
            )}
            <Box data-testid="bundle-changes-assets">
              <Typography variant="subtitle2">{t('brands.bundle.assets', 'Images')}</Typography>
              {assetCount === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('brands.bundle.noChanges', 'No changes')}
                </Typography>
              ) : (
                ASSET_GROUPS.filter((g) => assets[g].length > 0).map((g) => (
                  <Typography key={g} variant="body2" data-testid={`bundle-assets-${g}`}>
                    {`${t(`brands.bundle.${g}`, g)}: ${assets[g].join(', ')}`}
                  </Typography>
                ))
              )}
            </Box>
          </Stack>
        </Box>

        {r.warnings.length > 0 ? (
          <Box>
            <Typography variant="h3" gutterBottom>
              {t('brands.bundle.warnings', 'Warnings')}
            </Typography>
            <Stack spacing={1}>
              {r.warnings.map((w, i) => (
                <Alert
                  key={`${w.code}|${i}`}
                  severity={HIGHLIGHTED_WARNINGS.has(w.code) ? 'warning' : 'info'}
                  data-code={w.code}
                >
                  {w.message}
                </Alert>
              ))}
            </Stack>
          </Box>
        ) : null}

        <Box data-testid="bundle-contrast">
          <Typography variant="h3" gutterBottom>
            {t('brands.bundle.contrast', 'Contrast')}
          </Typography>
          {r.contrast.failures.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('brands.bundle.contrastOk', 'All text meets WCAG AA')}
            </Typography>
          ) : (
            <Box component="ul" sx={{ p: 0, m: 0 }}>
              {r.contrast.failures.map((f) => {
                const key = failureKey(f);
                const on = highlighted === key;
                return (
                  <ContrastFailureRow
                    key={key}
                    failure={f}
                    highlighted={on}
                    onToggle={() => setHighlighted(on ? null : key)}
                  />
                );
              })}
            </Box>
          )}
        </Box>

        {r.errors.length > 0 ? (
          <Box data-testid="bundle-errors">
            <Typography variant="h3" gutterBottom>
              {t('brands.bundle.errors', 'Errors')}
            </Typography>
            {zipAlert ? (
              <Stack spacing={1}>
                <Alert severity="error" data-testid={zipAlert.testId}>
                  {zipAlert.text}
                </Alert>
                <Accordion variant="outlined" disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      {t('brands.bundle.details', 'Details')}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>{errorRows(r.errors)}</AccordionDetails>
                </Accordion>
              </Stack>
            ) : (
              errorRows(r.errors)
            )}
          </Box>
        ) : null}
      </Stack>
    );
  }

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="bundle-dialog-title"
    >
      <DialogTitle id="bundle-dialog-title">
        {t('brands.bundle.title', 'Import bundle')}
      </DialogTitle>
      <DialogContent dividers>
        {report ? (
          renderReport(report)
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t(
                'brands.bundle.intro',
                'Upload a bundle exported from a brand. It is checked first; nothing changes until you apply it.',
              )}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <Button variant="outlined" component="label" disabled={busy}>
                {t('brands.bundle.chooseFile', 'Choose file')}
                <input
                  hidden
                  type="file"
                  accept=".zip,application/zip"
                  aria-label={t('brands.bundle.chooseFile', 'Choose file')}
                  onChange={handleFileChange}
                />
              </Button>
              <Typography variant="body2" data-testid="bundle-file-name">
                {file ? file.name : t('brands.bundle.noFile', 'No file chosen')}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" data-testid="bundle-flat-zip-hint">
              {t(
                'brands.bundle.flatZipHint',
                "Zip the bundle's files themselves — manifest.json, theme.json, copy.json, assets/ — not the folder that contains them. On a Mac, don't use Finder's Compress; the bundle's README has the zip command.",
              )}
            </Typography>
            {/* SASADM-0172 (SASATH-0389) — theme engine 6 changed every themeVersion once. */}
            <Alert severity="info" data-testid="bundle-reexport-note">
              {t(
                'brands.bundle.reexportNote',
                "Bundles exported before 12 Oct 2026 (theme engine 6) can't be imported to the same brand. Export again first.",
              )}
            </Alert>
            {tooLarge ? (
              <Alert severity="error">
                {t('brands.bundle.tooLarge', 'This bundle is over 10 MB')}
              </Alert>
            ) : null}
          </Stack>
        )}
        {error ? (
          <Alert severity="error" data-testid="bundle-error" sx={{ mt: 2 }}>
            {errorMessage(error)}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('brands.bundle.close', 'Close')}
        </Button>
        {report ? (
          <>
            <Button onClick={reset} disabled={busy}>
              {t('brands.bundle.chooseAnother', 'Choose another file')}
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleApply()}
              disabled={!report.ok || busy}
              startIcon={applying ? <CircularProgress size={16} /> : null}
            >
              {t('brands.bundle.apply', 'Apply')}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={() => void handleCheck()}
            disabled={!file || tooLarge || busy}
            startIcon={checking ? <CircularProgress size={16} /> : null}
          >
            {t('brands.bundle.check', 'Check bundle')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
